/**
 * Appels de compte : creation, connexion, profil.
 *
 * Le socle serveur N'EXISTE PAS ENCORE. Ces fonctions visent deja les routes
 * definitives et remontent une erreur franche tant qu'elles repondent 404 :
 * simuler une session ici masquerait precisement le bug qu'on chercherait
 * ensuite. Le jour ou le serveur repond, il n'y a rien a changer de ce cote.
 *
 * SESSION : cookie HttpOnly pose par le serveur, jamais lu par ce code (c'est
 * tout l'interet : un XSS ne peut pas le voler). D'ou `credentials: 'include'`
 * sur chaque appel, et une reponse du serveur qui doit porter les en-tetes CORS
 * correspondants.
 */
import { serverHttpBase } from './connection.js';
import type { AccountProfile } from '../account/accountStore.js';

/**
 * Meme hote que le jeu, en HTTP.
 *
 * On REUTILISE la resolution de connection.ts au lieu d'en refaire une : elle
 * connait le cas du developpement, ou le front est servi sur 5173 et le serveur
 * ecoute sur 9001. Ma premiere version se contentait de l'origine courante, et
 * postait donc sur Vite, qui repondait 404.
 */
const api = (path: string): string => `${serverHttpBase()}${path}`;

export class AccountError extends Error {}

async function call<T>(path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(api(path), {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'include',
      ...(body === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
    });
  } catch {
    throw new AccountError('offline');
  }

  if (response.status === 404 || response.status === 501) throw new AccountError('unavailable');
  if (!response.ok) throw new AccountError(await response.text().catch(() => 'unknown'));
  return (await response.json()) as T;
}

export const account = {
  /** Profil courant, ou null si le cookie ne vaut rien. */
  me: () => call<AccountProfile | null>('/auth/me'),
  create: (email: string, password: string) => call<AccountProfile>('/auth/register', { email, password }),
  signIn: (email: string, password: string) => call<AccountProfile>('/auth/login', { email, password }),
  signOut: () => call<void>('/auth/logout', {}),
  /** Ouvre une session de paiement Stripe et rend son URL. */
  checkout: () => call<{ url: string }>('/purchase/session', {}),
  /**
   * Retour de Stripe : fait trancher le serveur TOUT DE SUITE, plutot que
   * d'attendre le webhook. Rend le profil a jour, paye ou non.
   */
  confirm: (sessionId: string) => call<AccountProfile>('/purchase/confirm', { sessionId }),
  /** Demande un lien de reinitialisation. Repond 200 meme sur adresse inconnue. */
  forgot: (email: string) => call<null>('/auth/forgot', { email }),
  /** Consomme le lien recu par courriel. Connecte au passage. */
  reset: (token: string, password: string) => call<AccountProfile>('/auth/reset', { token, password }),
  /** Pseudo et avatar. Le serveur refuse un avatar payant sans achat. */
  profile: (nickname: string, avatar: string | null) =>
    call<AccountProfile>('/auth/profile', { nickname, avatar }),
  /** Le navigateur quitte la page : Google renvoie ensuite sur /account. */
  google: (): void => {
    window.location.href = api('/auth/google');
  },
};
